import { useEffect, useRef } from 'react';
import gql from 'graphql-tag';
import _ from 'lodash';
import { useMutation } from '@apollo/client';
import { useLocation } from 'react-router';
import * as Sentry from '@sentry/browser';
import { useStorage } from '~Components/Storage';

import { useDispatch, useSelector, useLogger } from '~/Hooks';

import { createPosOrder, createPosOrderVariables } from './__queries__';
import { ORDER_STATUS } from '~../__queries__/globalTypes';
import * as actions from '~actions';
import { useReportsApi } from '~Components/ReportsUniverse';
import useI18n from '~Translations';

const SYNC_MUTATION = gql`
  mutation createPosOrder(
    $basketToken: String!
    $paymentType: String!
    $creationDate: DateTime!
    $itemsJson: Json!
    $venueId: ID!
    $paymentId: ID
    $discountRate: Int
    $discountType: String
    $staffId: String
    $serviceType: String!
    $invoiceTargetId: ID
    $invoiceTargetName: String
    $desiredTime: String
    $orderStatus: ORDER_STATUS
    $origin: ORDER_ORIGIN
    $easyId: String
    $subPayments: [SubPaymentInput!]
    $transactionId: String
    $deviceId: String
    $tip: Int
    $zoneId: String
    $complianceId: String
    $cardDetails: CardDetailsInput
    $userId: ID
    $bookingId: String
  ) {
    createPosOrder(
      basketToken: $basketToken
      paymentType: $paymentType
      creationDate: $creationDate
      itemsJson: $itemsJson
      venueId: $venueId
      paymentId: $paymentId
      discountRate: $discountRate
      discountType: $discountType
      staffId: $staffId
      serviceType: $serviceType
      invoiceTargetId: $invoiceTargetId
      invoiceName: $invoiceTargetName
      desiredTime: $desiredTime
      orderStatus: $orderStatus
      origin: $origin
      easyId: $easyId
      subPayments: $subPayments
      transactionId: $transactionId
      deviceId: $deviceId
      tip: $tip
      zoneId: $zoneId
      complianceId: $complianceId
      cardDetails: $cardDetails
      userId: $userId
      bookingId: $bookingId
    ) {
      success
      message
    }
  }
`;

interface Props {
  orderId: string;
  complianceId?: string;
  creationDate?: Date;
  isStorageOrder?: boolean;
}

const OrderSyncer: React.FunctionComponent<Props> = ({
  orderId,
  complianceId,
  creationDate,
  isStorageOrder,
}) => {
  const isSynced = useRef(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const [storage, updateStorage] = useStorage();
  const country = useSelector((state) => state.country);
  const [, , venueId] = location.pathname.split('/');

  const storageRef = useRef(storage);
  useEffect(() => {
    storageRef.current = storage;
  }, [storage]);

  const errors = useSelector((state) => state.errors);
  const errorsRef = useRef(errors);

  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  const staffId = useSelector((state) => state.staff?.id);

  /**
   * Get the order from orders for sync state.
   *
   * If this is an order that has been stored in offline storage,
   * we need to get it from the storage state instead.
   */
  let syncData = useSelector((state) =>
    state.POSOrdersForSync.find((o) => o.order.id === orderId),
  )!;

  if (isStorageOrder) {
    syncData = storage.storedOrders.find((o) => o.order.id === orderId)!;
  }

  /**
   * Add the order for sync with kivra
   */
  useEffect(() => {
    // Make sure there's no duplicates
    const kivraOrders = storage.ordersForKivraSync.filter((o) => o.order?.id !== orderId);

    updateStorage({
      ordersForKivraSync: [...kivraOrders, syncData],
    });
  }, [syncData]);

  const reportsApi = useReportsApi();
  const receiptCounter = useSelector((state) => state.POSReceiptCounter);
  const logger = useLogger('order-syncer');
  const { i18n } = useI18n();

  const { amount, payment, order } = syncData;

  const itemsJson = order.items.map((item) => {
    return {
      ..._.pick(item, [
        'itemId',
        'entryId',
        'amount',
        'fraction',
        'discount',
        'vatRate',
        'fraction',
        'message',
      ]),
      amount: item.fraction ? item.amount / item.fraction : item.amount,
      discount: item.fraction ? item.discount / item.fraction : item.discount,
      variations: item.variations.map((v) => v.id),
      removedVariations: item.removedVariations.map((rv) => rv.id),
      // Hashmap {[variationId]: [entryId]}
      variationsAsItems: item.variations
        .filter((v) => Boolean(v.asItem?.entryId))
        .reduce((acc, v) => ({ ...acc, [v.id]: v.asItem!.entryId }), {}),
      message: item.manualName ? '[cn] ' + item.message : item.message,
    };
  });

  const [sync] = useMutation<createPosOrder, createPosOrderVariables>(SYNC_MUTATION);

  useEffect(() => {
    if (!order.items.length) {
      console.warn('No items');
      return;
    }

    /**
     * This gets picked up by Sentry for future use
     */
    Sentry.addBreadcrumb({
      message: JSON.stringify(order, null, 2),
    });
    console.log(JSON.stringify(order, null, 2));

    const orderStatus =
      order.defaultOrderStatus === ORDER_STATUS.COMPLETED
        ? ORDER_STATUS.COMPLETED
        : ORDER_STATUS.ACCEPTED;

    const tip = payment.meta.tip ? Math.round(payment.meta.tip) : payment.meta.tip;
    const subPayments =
      order.subPayments && order.subPayments.length > 0
        ? order.subPayments.map((sp) => ({
            ...sp,
            amount: sp.amount ? Math.round(sp.amount) : sp.amount,
            tip: sp.tip ? Math.round(sp.tip) : sp.tip,
          }))
        : undefined;

    const creationDateVar = creationDate || new Date();
    const variables: { variables: createPosOrderVariables } = {
      variables: {
        basketToken: `${
          storage.taxComplianceDeviceId
        }-${receiptCounter}-${storage.taxComplianceProductionNumber ||
          ''}-${storage.taxComplianceControlUnitSerial || ''}`,
        paymentType: payment.method,
        itemsJson,
        discountRate: undefined,
        discountType: undefined,
        venueId,
        staffId: order.staffId || staffId,
        orderStatus,
        creationDate: creationDateVar,
        subPayments,
        desiredTime: order.desiredTime,
        transactionId: payment.meta.transactionId,
        serviceType: order.serviceType,
        invoiceTargetId: payment.meta.invoiceTarget,
        invoiceTargetName: payment.meta.invoiceTargetName!,
        origin: order.origin,
        tip,
        easyId: order.orderNumber,
        deviceId: storage.taxComplianceDeviceId || '',
        zoneId: order.zoneId ?? undefined,
        complianceId,
        cardDetails: payment.meta?.cardDetails,
        userId: order.userId ?? undefined,
        bookingId: order.bookingId ?? undefined,
      },
    };

    const run = async () => {
      if (isSynced.current) {
        return;
      }

      logger.info(`order-syncer: Starting sync for order ${order.id}`, { orderId, variables });

      sync(variables)
        .then((res) => {
          if (res.data?.createPosOrder.success) {
            const payments =
              (order.subPayments || [])?.length > 0
                ? order.subPayments!.map((sp) => ({
                    amount: sp.amount,
                    name: sp.type,
                  }))
                : [
                    {
                      amount,
                      name: payment.method,
                    },
                  ];

            try {
              const orderForReport = {
                items: order.items.map((item) => ({
                  amount: item.amount,
                  fraction: item.fraction,
                  category: item.categoryId,
                  vatRate: item.vatRate,
                  discount: item.discount,
                  itemId: item.itemId,
                  message: item.message,
                  manualName: item.manualName,
                })),
                staffId,
                paymentMethods: payments,
                tip: payment.meta.tip || 0,
              };

              reportsApi.addOrder(
                order.id,
                orderForReport.items,
                orderForReport.paymentMethods,
                payment.meta.tip,
                staffId,
              );
            } catch (e) {
              logger.error((e as Error).message);
              logger.error(`order-syncer: Adding order ${order.id} to reports failed`, {
                orderId,
                errorObject: e,
              });
            }

            isSynced.current = true;

            dispatch({
              type: 'POS_ORDER_SYNCED',
              id: order.id,
            });

            if (isStorageOrder) {
              updateStorage({
                storedOrders: storageRef.current.storedOrders.filter(
                  (o) => o.order.id !== orderId,
                ),
              });
            }

            logger.info(`order-syncer: Sync for order ${order.id} successful`, { orderId });

            // Clear all potential existing order sync errors for this order
            const orderSyncErrors = errorsRef.current.filter(
              (e) => e.orderId === order.id && e.errorType === 'ORDER_SYNC',
            );

            if (orderSyncErrors.length > 0) {
              // Clear all matching errors one by one
              orderSyncErrors.forEach((error) => {
                dispatch({
                  type: actions.CLEAR_ERROR,
                  id: error.id,
                });
              });

              logger.info(
                `order-syncer: Cleared ${orderSyncErrors.length} order sync errors for order ${order.id}`,
              );
            }
          } else {
            logger.error(`order-syncer: Sync for order ${order.id} failed`, {
              orderId,
              variables,
            });
            throw new Error(res.data?.createPosOrder.message);
          }
        })
        .then(() => Promise.resolve())
        .catch((err) => {
          setTimeout(() => {
            run();
          }, 5000);

          const errorMessage = err ? `${err}` : i18n('Ordersyncer.NetworkError');

          dispatch(
            actions.addError({
              errorType: 'ORDER_SYNC',
              orderId: order.id,
              title: errorMessage,
              message: i18n('Ordersyncer.PleaseSaveBackup'),
            }),
          );
          logger.error(`order-syncer: Sync for order ${order.id} failed, retrying...`, {
            orderId,
            variables,
            errorMessage: errorMessage,
          });
        });
    };

    run();
  }, []);

  return null;
};

export default OrderSyncer;
